'use client';

import { useCallback, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  useAskEllieSidebarStore,
  clampSidebarWidth,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
} from '@/state/askEllieSidebarState';

export function AskEllieSidebarResizeHandle() {
  const setWidth = useAskEllieSidebarStore((s) => s.setWidth);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  // Reference to the sidebar element for direct DOM manipulation during drag
  const sidebarElRef = useRef<HTMLElement | null>(null);
  // Track active drag listeners so unmount mid-drag can clean them up
  const activeMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const activeUpRef = useRef<((e: MouseEvent) => void) | null>(null);

  const detachDragListeners = useCallback(() => {
    if (activeMoveRef.current) {
      document.removeEventListener('mousemove', activeMoveRef.current);
      activeMoveRef.current = null;
    }
    if (activeUpRef.current) {
      document.removeEventListener('mouseup', activeUpRef.current);
      activeUpRef.current = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Safety net — if the component unmounts mid-drag, drop the document
  // listeners and reset body styles so we don't leak handlers.
  useEffect(() => {
    return () => {
      isDraggingRef.current = false;
      sidebarElRef.current = null;
      detachDragListeners();
    };
  }, [detachDragListeners]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Defensive: clean up any stale listeners before starting a new drag.
      detachDragListeners();
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = useAskEllieSidebarStore.getState().width;

      // Find the sidebar element (next sibling of the resize handle)
      const handleEl = e.currentTarget as HTMLElement;
      sidebarElRef.current = handleEl.nextElementSibling as HTMLElement | null;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDraggingRef.current) return;
        // Dragging left increases sidebar width, dragging right decreases it
        const delta = startXRef.current - moveEvent.clientX;
        const newWidth = clampSidebarWidth(startWidthRef.current + delta);
        // Update DOM directly for smooth drag — avoid re-rendering on every pixel
        if (sidebarElRef.current) {
          sidebarElRef.current.style.width = `${newWidth}px`;
        }
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        isDraggingRef.current = false;
        // Commit final width to Zustand store (single re-render)
        const delta = startXRef.current - upEvent.clientX;
        const finalWidth = clampSidebarWidth(startWidthRef.current + delta);
        setWidth(finalWidth);
        sidebarElRef.current = null;
        detachDragListeners();
      };

      activeMoveRef.current = handleMouseMove;
      activeUpRef.current = handleMouseUp;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [setWidth, detachDragListeners]
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      className={cn(
        'relative flex-shrink-0 w-[7px] cursor-col-resize',
        'bg-ds-surface-off-white',
        'hover:bg-ds-brand-lightest',
        'transition-colors duration-150',
        'border-x border-ds-border-subtlest'
      )}
      title={`Resize sidebar (${SIDEBAR_MIN_WIDTH}\u2013${SIDEBAR_MAX_WIDTH}px)`}
    >
      {/* Centered pill indicator */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[3px] h-[31px] rounded-full bg-ds-gray-subtle opacity-60" />
    </div>
  );
}
